import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ClerkProvider, useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { setOnUnauthorized } from '@/api/api-client';
import { SessionContext, type SessionState } from '@/contexts/SessionContext';
import {
  clearSession as clearStoredSession,
  clearToken,
  getStoredSession,
  getStoredToken,
  storeSession,
  storeToken,
  type StoredSession,
} from '@/utils/session';
import { exchangeClerkSession } from './clerk-session';
import i18n, { clerkLocalization } from '@/i18n';

function ClerkSessionInner({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const { user } = useUser();
  const syncedUsername = useRef<string | null>(null);
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  // Bumped by the 401 handler to force a fresh exchange from the live Clerk session.
  const [exchangeAttempt, setExchangeAttempt] = useState(0);

  useEffect(() => {
    setOnUnauthorized(() => {
      clearToken();
      clearStoredSession();
      setExchangeAttempt(n => n + 1);
    });
    return () => setOnUnauthorized(null);
  }, []);

  // The username changed in Clerk's profile modal: drop the stored session so the next
  // exchange carries the new name to the backend, which is what the leaderboard shows.
  useEffect(() => {
    if (state.status !== 'ready' || !user?.username) return;
    if (user.username === state.session.username || syncedUsername.current === user.username)
      return;
    syncedUsername.current = user.username;
    clearToken();
    clearStoredSession();
    setExchangeAttempt(n => n + 1);
  }, [user?.username, state]);

  useEffect(() => {
    let cancelled = false;

    const sync = async () => {
      if (!isLoaded) return;

      if (!isSignedIn) {
        clearToken();
        clearStoredSession();
        if (!cancelled) setState({ status: 'signedOut' });
        return;
      }

      const storedToken = getStoredToken();
      const storedSession = getStoredSession();
      if (storedToken && storedSession) {
        if (!cancelled) setState({ status: 'ready', session: storedSession });
        return;
      }

      if (!cancelled) setState({ status: 'loading' });
      try {
        // A forced exchange must not reuse a cached Clerk token that still carries the old username.
        const clerkToken = await getToken({ skipCache: exchangeAttempt > 0 });
        if (!clerkToken) {
          throw new Error('Clerk session token is unavailable');
        }
        const result = await exchangeClerkSession(clerkToken);
        if (cancelled) return;
        const session: StoredSession = {
          userId: result.user.sso_user_id,
          username: result.user.username,
        };
        storeToken(result.token);
        storeSession(session);
        setState({ status: 'ready', session });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : i18n.t('app.signInFailed');
        setState({ status: 'error', message });
      }
    };

    sync();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken, exchangeAttempt]);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}

interface ClerkSessionProviderProps {
  publishableKey: string;
  children: ReactNode;
}

/**
 * Clerk-backed session provider — the squads counterpart of the pickem
 * frontend's ClerkTenantAuthProvider. Clerk renders the hosted sign-in /
 * sign-up forms at /sign-in and /sign-up (email + password + username, with
 * Google available as a social button); once signed in, the Clerk session JWT
 * is exchanged for the internal application JWT via
 * POST /api/auth/clerk/session, which `apiClient` then sends on every
 * /api/squads/* request.
 */
export function ClerkSessionProvider({ publishableKey, children }: ClerkSessionProviderProps) {
  useTranslation(); // re-render on a language switch so Clerk's forms follow it
  return (
    <ClerkProvider
      publishableKey={publishableKey}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      afterSignOutUrl="/"
      localization={clerkLocalization()}
    >
      <ClerkSessionInner>{children}</ClerkSessionInner>
    </ClerkProvider>
  );
}
